import React, { useEffect, useRef, useState } from "react";
import './Css/FinishingSchedule.css';
import './Css/FinishingSchedule-responsive.css';
import { useNavigate, useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../Controllers/hooks";
import { useSelector } from "react-redux";
import { RootState } from "../../Controllers/Store";
import { hasPermission } from "../../Controllers/Selectors/permissionsSelector";

import { Ripple } from "primereact/ripple";
import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Checkbox } from "primereact/checkbox";
import { Tooltip } from "primereact/tooltip";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";

import NoDataCreated from "../../Components/NoDataCreated";
import PageLoader from "../../Components/PageLoader";
import ErrorPage from "../../Components/ErrorPage";
import { goToTop } from "../../Components/goToTop";
import { getAppCurrencyType, useGoBack } from "../../Utils/Common";

import {
    fetchFSProjectList,
    fetchFSApplianceList,
    fetchFSBrandList,
    fetchFSColorList,
    fetchFSItemList,
    fetchFSLocationList,
    fetchFSSizeList,
    fetchFSTypeList,
    fetchFSPaintList,
    fetchFSAccessoryNameList,
    fetchFSAccessorySubNameList,
    fetchFSFloorFinishingList,
    fetchFSTileList,
    fetchFSTileShopList,
    fetchFSDoorList,
    fetchFSDoorSizeList
} from "../../Controllers/Thunks/FinishingScheduleThunks";
import { FSFeature, FSProject } from "../../Controllers/Types/FinishingSchedule/FinishingSchedule";
import { FSLocation } from "../../Controllers/Types/FinishingSchedule/Location";
import { FSColor } from "../../Controllers/Types/FinishingSchedule/Color";
import { FSBrand } from "../../Controllers/Types/FinishingSchedule/Brand";
import { FSType } from "../../Controllers/Types/FinishingSchedule/Type";
import { FSTileShop } from "../../Controllers/Types/FinishingSchedule/Tile/TileShop";
import { FSTile, FSTileData } from "../../Controllers/Types/FinishingSchedule/Tile/Tile";
import { confirmDialog } from "primereact/confirmdialog";
import { checkFileOtherList, checkFileOthers, checkModelSelected, checkNumber, checkText } from "../../Utils/Validator";
import TextInput from "../../Components/TextInput";
import { InputText } from "primereact/inputtext";

const FinishingSchedule: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const goBack = useGoBack();
    const toast = useRef<Toast>(null);
    const today = new Date();
    const currencySymbol = getAppCurrencyType();

    const {
        loadingFSProjects, errorFSProjects, fsProjects,
        loadingFSAppliances, errorFSAppliances, fsAppliances,
        loadingFSBrands, errorFSBrands, fsBrands,
        loadingFSColors, errorFSColors, fsColors,
        loadingFSDocuments, errorFSDocuments, fsDocuments,
        loadingFSItems, errorFSItems, fsItems,
        loadingFSLocations, errorFSLocations, fsLocations,
        loadingFSSizes, errorFSSizes, fsSizes,
        loadingFSTypes, errorFSTypes, fsTypes,
        loadingFSPaints, errorFSPaints, fsPaints,
        loadingFSAccessoryNames, errorFSAccessoryNames, fsAccessoryNames,
        loadingFSAccessorySubNames, errorFSAccessorySubNames, fsAccessorySubNames,
        loadingFSFloorFinishings, errorFSFloorFinishings, fsFloorFinishings,
        loadingFSTiles, errorFSTiles, fsTiles,
        loadingFSTileShops, errorFSTileShops, fsTileShops,
        loadingFSDoors, errorFSDoors, fsDoors,
        loadingFSDoorSizes, errorFSDoorSizes, fsDoorSizes
    } = useAppSelector((state) => state?.finishingSchedule);

    const [loading, setLoading] = useState<boolean>(false);
    const [initialDataLoading, setInitialDataLoading] = useState<boolean>(false);
    const [dataState, setDataState] = useState<"Add" | "Edit">("Add");
    const [navigateFrom, setNavigateFrom] = useState<string | null>(null);
    const [isOwnProject, setIsOwnProject] = useState<boolean>(false);
    const [customLoading, setCustomLoading] = useState<boolean>(false);
    const [customDataState, setCustomDataState] = useState<"Add" | "Edit">("Add");

    const [finishingScheduleProject, setFinishingScheduleProject] = useState<FSProject | null>(null);
    const [finishingScheduleFeatures, setFinishingScheduleFeatures] = useState<FSFeature[]>([]);
    const [activeFSFeature, setActiveFSFeature] = useState<FSFeature | null>(null);

    /* ************************************************************* Location */
    const [showLocationListModal, setShowLocationListModal] = useState<boolean>(false);
    const [selectedFSLocation, setSelectedFSLocation] = useState<FSLocation | null>(null);
    const [customLocationName, setCustomLocationName] = useState<string>('');
    const [customLocationNameError, setCustomLocationNameError] = useState<string>('');
    /* ************************************************************* */

    /* ************************************************************* Color */
    const [showColorListModal, setShowColorListModal] = useState<boolean>(false);
    const [selectedFSColor, setSelectedFSColor] = useState<FSColor | null>(null);
    const [customColorName, setCustomColorName] = useState<string>('');
    const [customColorNameError, setCustomColorNameError] = useState<string>('');
    /* ************************************************************* */

    /* ************************************************************* Brand */
    const [showBrandListModal, setShowBrandListModal] = useState<boolean>(false);
    const [selectedFSBrand, setSelectedFSBrand] = useState<FSBrand | null>(null);
    const [customBrandName, setCustomBrandName] = useState<string>('');
    const [customBrandNameError, setCustomBrandNameError] = useState<string>('');
    /* ************************************************************* */

    /* ************************************************************* Type */
    const [showTypeListModal, setShowTypeListModal] = useState<boolean>(false);
    const [fsTypesData, setFSTypesData] = useState<FSType[]>([]);
    const [fsTypeSearchKey, setFSTypeSearchKey] = useState<string>('');
    const [selectedFSType, setSelectedFSType] = useState<FSType | null>(null);
    const [customTypeName, setCustomTypeName] = useState<string>('');
    const [customTypeNameError, setCustomTypeNameError] = useState<string>('');
    const [selectedCustomType, setSelectedCustomType] = useState<FSType | null>(null);
    const [customTypeUpdateHasChanges, setCustomTypeUpdateHasChanges] = useState<boolean>(false);
    const [deleteTypeLoadingId, setDeleteTypeLoadingId] = useState<number | null>(null);
    /* ************************************************************* */

    /* ************************************************************* Tile shop */
    const [showTileShopListModal, setShowTileShopListModal] = useState<boolean>(false);
    const [fsTileShopsData, setFSTileShopsData] = useState<FSTileShop[]>([]);
    const [fsTileShopSearchKey, setFSTileShopSearchKey] = useState<string>('');
    const [selectedFSTileShop, setSelectedFSTileShop] = useState<FSTileShop | null>(null);
    const [customTileShopName, setCustomTileShopName] = useState<string>('');
    const [customTileShopNameError, setCustomTileShopNameError] = useState<string>('');
    const [selectedCustomTileShop, setSelectedCustomTileShop] = useState<FSTileShop | null>(null);
    const [customTileShopUpdateHasChanges, setCustomTileShopUpdateHasChanges] = useState<boolean>(false);
    const [deleteTileShopLoadingId, setDeleteTileShopLoadingId] = useState<number | null>(null);
    /* ************************************************************* */

    /* ************************************************************* Door feature fields */
    // errors
    /* ************************************************************* */

    /* ************************************************************* Paint feature fields */
    // errors
    /* ************************************************************* */

    /* ************************************************************* Tiles feature fields */
    const [showTilesModal, setShowTilesModal] = useState<boolean>(false);
    const [tileLocation, setTileLocation] = useState<string>('');
    const [tileLocationUpdateHasChanges, setTileLocationUpdateHasChanges] = useState<boolean>(false);
    const [tileDetailUpdateHasChanges, setTileDetailUpdateHasChanges] = useState<boolean>(false);
    const [tileLocationDeleteLoadingId, setTileLocationDeleteLoadingId] = useState<number | null>(null);
    const [tileDetailDeleteLoadingId, setTileDetailDeleteLoadingId] = useState<number | null>(null);
    const [selectedTile, setSelectedTile] = useState<FSTile | null>(null);
    const [selectedTileDetail, setSelectedTileDetail] = useState<FSTileData | null>(null);
    const [showTileDetailModal, setShowTileDetailModal] = useState<boolean>(false);
    const [tileName, setTileName] = useState<string>('');
    const [tileQuantity, setTileQuantity] = useState<number | null>(null);
    const [selectedTileShop, setSelectedTileShop] = useState<FSTileShop | null>(null);
    const [selectedTileColor, setSelectedTileColor] = useState<FSColor | null>(null);
    const [tileSize, setTileSize] = useState<number | null>(null);
    const [tileLink, setTileLink] = useState<string>('');
    const [tileImageFile, setTileImageFile] = useState<File | null>(null);
    const [tileImageFileName, setTileImageFileName] = useState<string>("");
    const [tileFiles, setTileFiles] = useState<File[]>([]);

    // errors
    const [tileLocationError, setTileLocationError] = useState<string>('');
    const [tileTypeError, setTileTypeError] = useState<string>('');
    const [tileNameError, setTileNameError] = useState<string>('');
    const [tileQuantityError, setTileQuantityError] = useState<string>('');
    const [tileShopError, setTileShopError] = useState<string>('');
    const [tileColorError, setTileColorError] = useState<string>('');
    const [tileSizeError, setTileSizeError] = useState<string>('');
    const [tileLinkError, setTileLinkError] = useState<string>('');
    const [tileImageFileError, setTileImageFileError] = useState<string>('');
    const [tileFilesError, setTileFilesError] = useState<string>('');
    /* ************************************************************* */

    /* ************************************************************* Floor finishing feature fields */
    // errors
    /* ************************************************************* */

    /* ************************************************************* Permission handler */
    const useCanEdit = (screen: string, permission: string) => {
        return useSelector((state: RootState) =>
            hasPermission(state, screen, permission)
        );
    };

    const hasPermissionForAddBrand = useCanEdit("Finishing Schedule", "add_brand");
    const hasPermissionForUpdateBrand = useCanEdit("Finishing Schedule", "update_brand");
    const hasPermissionForDeleteBrand = useCanEdit("Finishing Schedule", "delete_brand");
    const hasPermissionForAddColor = useCanEdit("Finishing Schedule", "add_color");
    const hasPermissionForUpdateColor = useCanEdit("Finishing Schedule", "update_color");
    const hasPermissionForDeleteColor = useCanEdit("Finishing Schedule", "delete_color");
    const hasPermissionForAddLocation = useCanEdit("Finishing Schedule", "add_location");
    const hasPermissionForUpdateLocation = useCanEdit("Finishing Schedule", "update_location");
    const hasPermissionForDeleteLocation = useCanEdit("Finishing Schedule", "delete_location");
    const hasPermissionForDeleteFile = useCanEdit("Finishing Schedule", "delete_file");
    const hasPermissionForAddAccessory = useCanEdit("Finishing Schedule", "add_accessory");
    const hasPermissionForUpdateAccessory = useCanEdit("Finishing Schedule", "update_accessory");
    const hasPermissionForDeleteAccessory = useCanEdit("Finishing Schedule", "delete_accessory");
    const hasPermissionForAddSubAccessory = useCanEdit("Finishing Schedule", "add_sub_accessory");
    const hasPermissionForUpdateSubAccessory = useCanEdit("Finishing Schedule", "update_sub_accessory");
    const hasPermissionForDeleteSubAccessory = useCanEdit("Finishing Schedule", "delete_sub_accessory");
    const hasPermissionForUpdatePaint = useCanEdit("Finishing Schedule", "update_paint");
    const hasPermissionForDeletePaint = useCanEdit("Finishing Schedule", "delete_paint");
    const hasPermissionForAddTileLocation = useCanEdit("Finishing Schedule", "add_tile_location");
    const hasPermissionForUpdateTileLocation = useCanEdit("Finishing Schedule", "update_tile_location");
    const hasPermissionForDeleteTileLocation = useCanEdit("Finishing Schedule", "delete_tile_location");
    const hasPermissionForAddTileShop = useCanEdit("Finishing Schedule", "add_tile_shop");
    const hasPermissionForUpdateTileShop = useCanEdit("Finishing Schedule", "update_tile_shop");
    const hasPermissionForDeleteTileShop = useCanEdit("Finishing Schedule", "delete_tile_shop");
    const hasPermissionForAddTile = useCanEdit("Finishing Schedule", "add_tile");
    const hasPermissionForUpdateTile = useCanEdit("Finishing Schedule", "update_tile");
    const hasPermissionForDeleteTile = useCanEdit("Finishing Schedule", "delete_tile");
    const hasPermissionForAddType = useCanEdit("Finishing Schedule", "add_type");
    const hasPermissionForUpdateType = useCanEdit("Finishing Schedule", "update_type");
    const hasPermissionForDeleteType = useCanEdit("Finishing Schedule", "delete_type");
    /* ************************************************************* */

    useEffect(() => {
        if (location?.state && location?.state?.navigateFrom) {
            setNavigateFrom(location?.state?.navigateFrom)
        } else {
            setNavigateFrom(null);
        }
    }, [location.state]);

    useEffect(() => {
        if (location.state && location.state.projectData) {
            const stateProject = location.state.projectData as FSProject;

            setIsOwnProject(stateProject.is_own_project!);

            const activeProject = fsProjects.filter(
                (e) => e.project_id === stateProject.project_id
            );

            if (activeProject.length > 0) {
                setFinishingScheduleProject(activeProject[0] as FSProject);
            } else {
                setFinishingScheduleProject(stateProject);
            }
        }
    }, [location.state, fsProjects]);

    useEffect(() => {
        finishingScheduleProjectDataGet();
    }, [dispatch]);

    const finishingScheduleProjectDataGet = async () => {
        setInitialDataLoading(true);
        await dispatch(fetchFSProjectList());
        setInitialDataLoading(false);
    }

    useEffect(() => {
        if (finishingScheduleProject?.feature_list?.length) {
            setFinishingScheduleFeatures(finishingScheduleProject.feature_list);
            setActiveFSFeature(finishingScheduleProject.feature_list[0]);
        } else {
            setFinishingScheduleFeatures([]);
            setActiveFSFeature(null);
        }
    }, [finishingScheduleProject]);

    useEffect(() => {
        if (!activeFSFeature) return;

        switch (activeFSFeature.feature) {
            case 'Door':
                fsDoorDataGet();
                break;
            case 'Paint':
                fsPaintDataGet();
                break;
            case 'Tiles':
                fsTileDataGet();
                break;
            case 'Floor Finishing':
                fsFloorFinishingDataGet();
                break;
            default:
                break;
        }
    }, [activeFSFeature]);

    const fsDoorDataGet = async () => {
        if (!activeFSFeature?.id) return;

        setInitialDataLoading(true);
        await dispatch(fetchFSDoorList(activeFSFeature?.id!));
        setInitialDataLoading(false);
    }

    const fsPaintDataGet = async () => {
        if (!activeFSFeature?.id) return;

        setInitialDataLoading(true);
        await dispatch(fetchFSPaintList(activeFSFeature?.id!));
        setInitialDataLoading(false);
    }

    const fsTileDataGet = async () => {
        if (!activeFSFeature?.id) return;

        setInitialDataLoading(true);
        await dispatch(fetchFSTileList(activeFSFeature?.id!));
        setInitialDataLoading(false);
    }

    const fsFloorFinishingDataGet = async () => {
        if (!activeFSFeature?.id) return;

        setInitialDataLoading(true);
        await dispatch(fetchFSFloorFinishingList(activeFSFeature?.id!));
        setInitialDataLoading(false);
    }

    const handleSelectFSFeature = (feature: FSFeature) => {
        setActiveFSFeature(feature);
        goToTop();
    }


    /* ************************************************************* Location */
    useEffect(() => {
    });

    const fsLocationDataGet = async () => {
        // await dispatch(fetchFSLocationList())
    }
    /* ************************************************************* */

    /* ************************************************************* Color */
    /* ************************************************************* */

    /* ************************************************************* Brand */
    /* ************************************************************* */

    /* ************************************************************* Type */
    useEffect(() => {
    })

    const fsTypesDataGet = async () => {
        // await dispatch(fetchFSTypeList(activeFSFeature?.feature));
    }

    useEffect(() => {
        if (fsTypes && fsTypes?.length > 0) {
            const filteredData = fsTypes.filter((type) =>
                type
                    ?.type_name!.toLowerCase()
                    .includes(fsTypeSearchKey.toLowerCase())
            );

            setFSTypesData(filteredData);
        } else {
            setFSTypesData([]);
        }
    }, [fsTypes, fsTypeSearchKey])

    const handleCloseTypeListModal = () => {
        setShowTypeListModal(false);
        setFSTypeSearchKey('');
        handleResetCustomFSTypeFields();
    }

    const handleResetCustomFSTypeFields = () => {
        setCustomTypeName('');
        setCustomTypeNameError('');
        setCustomDataState('Add');
        setSelectedCustomType(null);
    }

    const handleAddType = () => {
        setShowTypeListModal(true);
        setCustomDataState('Add');
    };

    const handleSelectType = (type: FSType) => {
        setSelectedFSType(type);

        setTimeout(() => {
            handleCloseTypeListModal();
        }, 200);
    };

    function showAPIErrorForType(keyName: string, error: string): void {
        if (keyName === "type_name") {
            setCustomTypeNameError(error);
        }
    }

    useEffect(() => {
        checkText(customTypeName, false, setCustomTypeNameError);
    }, [customTypeName])

    function checkCredentialsForType(): boolean {
        const isValidCustomTypeName = checkText(customTypeName, true, setCustomTypeNameError);

        return isValidCustomTypeName;
    }

    const handleCreateCustomFSType = async (e: React.FormEvent) => {
        e.preventDefault();

        // if (checkCredentialsForType()) {
        //     setCustomLoading(true);
        //     const data = await ; // api here

        //     setCustomLoading(false);
        //     if (data.statusCode === 200) {
        //         fsTypesDataGet();
        //         handleResetCustomFSTypeFields();

        //         if (toast.current) {
        //             toast.current.show({
        //                 severity: "success",
        //                 summary: "Success",
        //                 detail: "Type created successfully.",
        //                 life: 3000,
        //             });
        //         }
        //     } else if (data.statusCode === 422) {
        //         Object.entries(data.errors ?? []).map(([key, value], index) => (
        //             showAPIErrorForType(key, value[0])
        //         ));
        //     } else {
        //         if (toast.current) {
        //             toast.current.show({
        //                 severity: "error",
        //                 summary: "Failed",
        //                 detail: "There was an error creating the type.",
        //                 life: 3000,
        //             });
        //         }
        //     }
        // }
    };

    const handleEditCustomType = (type: FSType) => {
        if (type) {
            setSelectedCustomType(type);

            setCustomTypeName(type?.type_name || '');
            setCustomDataState('Edit');
        } else {
            setSelectedCustomType(null);
        }
    }

    useEffect(() => {
        if (selectedCustomType) {
            if (customTypeName && customTypeName !== selectedCustomType?.type_name) {
                setCustomTypeUpdateHasChanges(true);
            } else {
                setCustomTypeUpdateHasChanges(false);
            }
        }
    }, [selectedCustomType, customTypeName]);

    const handleUpdateCustomFSType = async (e: React.FormEvent) => {
        e.preventDefault();

        // if (checkCredentialsForType() && selectedCustomType !== null) {
        //     setCustomLoading(true);
        //     const data = await ; // api here

        //     setCustomLoading(false);
        //     if (data.statusCode === 200) {
        //         fsTypesDataGet();
        //         handleResetCustomFSTypeFields();

        //         if (toast.current) {
        //             toast.current.show({
        //                 severity: "success",
        //                 summary: "Success",
        //                 detail: "Type updated successfully.",
        //                 life: 3000,
        //             });
        //         }
        //     } else if (data.statusCode === 422) {
        //         Object.entries(data.errors ?? []).map(([key, value], index) => (
        //             showAPIErrorForType(key, value[0])
        //         ));
        //     } else {
        //         if (toast.current) {
        //             toast.current.show({
        //                 severity: "error",
        //                 summary: "Failed",
        //                 detail: "There was an error updating the type.",
        //                 life: 3000,
        //             });
        //         }
        //     }
        // }
    };

    const handleDeleteCustomFSType = (type: FSType) => {
        if (type) {
            handleResetCustomFSTypeFields();
            const customTypeId = type?.id;

            confirmDialog({
                message: "Are you sure you want to delete this type?",
                header: "Confirm the deletion",
                icon: "bi bi-info-circle",
                defaultFocus: "accept",
                acceptClassName: "p-button-danger",
                accept: () => deleteCustomFSType(customTypeId!),
                dismissableMask: true,
                resizable: false,
                draggable: false,
            });
        }
    }

    const deleteCustomFSType = async (customTypeId: number) => {
        // if (customTypeId) {
        //     setDeleteTypeLoadingId(customTypeId);
        //     const result = await; // api here

        //     setDeleteTypeLoadingId(null);
        //     if (result.statusCode === 200) {
        //         fsTypesDataGet();
        //         setSelectedFSType(null);

        //         if (toast.current) {
        //             toast.current.show({
        //                 severity: "success",
        //                 summary: "Success",
        //                 detail: "Type deleted successfully.",
        //                 life: 3000,
        //             });
        //         }
        //     } else {
        //         if (toast.current) {
        //             toast.current.show({
        //                 severity: "error",
        //                 summary: "Failed",
        //                 detail: "There was an error deleting the type.",
        //                 life: 3000,
        //             });
        //         }
        //     }
        // }
    }
    /* ************************************************************* */


    /* ************************************************************* Tile shop */
    useEffect(() => {
    })

    const fsTileShopsDataGet = async () => {
        await dispatch(fetchFSTileShopList());
    }

    useEffect(() => {
        if (fsTileShops && fsTileShops?.length > 0) {
            const filteredData = fsTileShops.filter((shop) =>
                shop
                    ?.shop_name!.toLowerCase()
                    .includes(fsTileShopSearchKey.toLowerCase())
            );

            setFSTileShopsData(filteredData);
        } else {
            setFSTileShopsData([]);
        }
    }, [fsTileShops, fsTileShopSearchKey])

    const handleCloseTileShopListModal = () => {
        setShowTileShopListModal(false);
        setFSTileShopSearchKey('');
        handleResetCustomFSTileShopFields();
    }

    const handleResetCustomFSTileShopFields = () => {
        setCustomTileShopName('');
        setCustomTileShopNameError('');
        setCustomDataState('Add');
        setSelectedCustomTileShop(null);
    }

    const handleAddTileShop = () => {
        setShowTileShopListModal(true);
        setCustomDataState('Add');
    };

    const handleSelectTileShop = (shop: FSTileShop) => {
        setSelectedFSTileShop(shop);

        setTimeout(() => {
            handleCloseTileShopListModal();
        }, 200);
    };

    function showAPIErrorForTileShop(keyName: string, error: string): void {
        if (keyName === "shop_name") {
            setCustomTileShopNameError(error);
        }
    }

    useEffect(() => {
        checkText(customTileShopName, false, setCustomTileShopNameError);
    }, [customTileShopName])

    function checkCredentialsForTileShop(): boolean {
        const isValidCustomTileShopName = checkText(customTileShopName, true, setCustomTileShopNameError);

        return isValidCustomTileShopName;
    }

    const handleCreateCustomFSTileShop = async (e: React.FormEvent) => {
        e.preventDefault();

        // if (checkCredentialsForTileShop()) {
        //     setCustomLoading(true);
        //     const data = await ; // api here

        //     setCustomLoading(false);
        //     if (data.statusCode === 200) {
        //         fsTileShopsDataGet();
        //         handleResetCustomFSTileShopFields();

        //         if (toast.current) {
        //             toast.current.show({
        //                 severity: "success",
        //                 summary: "Success",
        //                 detail: "Shop created successfully.",
        //                 life: 3000,
        //             });
        //         }
        //     } else if (data.statusCode === 422) {
        //         Object.entries(data.errors ?? []).map(([key, value], index) => (
        //             showAPIErrorForTileShop(key, value[0])
        //         ));
        //     } else {
        //         if (toast.current) {
        //             toast.current.show({
        //                 severity: "error",
        //                 summary: "Failed",
        //                 detail: "There was an error creating the shop.",
        //                 life: 3000,
        //             });
        //         }
        //     }
        // }
    };

    const handleEditCustomTileShop = (shop: FSTileShop) => {
        if (shop) {
            setSelectedCustomTileShop(shop);

            setCustomTileShopName(shop?.shop_name || '');
            setCustomDataState('Edit');
        } else {
            setSelectedCustomTileShop(null);
        }
    }

    useEffect(() => {
        if (selectedCustomTileShop) {
            if (customTileShopName && customTileShopName !== selectedCustomTileShop?.shop_name) {
                setCustomTileShopUpdateHasChanges(true);
            } else {
                setCustomTileShopUpdateHasChanges(false);
            }
        }
    }, [selectedCustomTileShop, customTileShopName]);

    const handleUpdateCustomFSTileShop = async (e: React.FormEvent) => {
        e.preventDefault();

        // if (checkCredentialsForTileShop() && selectedCustomTileShop !== null) {
        //     setCustomLoading(true);
        //     const data = await ; // api here

        //     setCustomLoading(false);
        //     if (data.statusCode === 200) {
        //         fsTileShopsDataGet();
        //         handleResetCustomFSTileShopFields();

        //         if (toast.current) {
        //             toast.current.show({
        //                 severity: "success",
        //                 summary: "Success",
        //                 detail: "Shop updated successfully.",
        //                 life: 3000,
        //             });
        //         }
        //     } else if (data.statusCode === 422) {
        //         Object.entries(data.errors ?? []).map(([key, value], index) => (
        //             showAPIErrorForTileShop(key, value[0])
        //         ));
        //     } else {
        //         if (toast.current) {
        //             toast.current.show({
        //                 severity: "error",
        //                 summary: "Failed",
        //                 detail: "There was an error updating the shop.",
        //                 life: 3000,
        //             });
        //         }
        //     }
        // }
    };

    const handleDeleteCustomFSTileShop = (shop: FSTileShop) => {
        if (shop) {
            handleResetCustomFSTileShopFields();
            const customTileShopId = shop?.id;

            confirmDialog({
                message: "Are you sure you want to delete this shop?",
                header: "Confirm the deletion",
                icon: "bi bi-info-circle",
                defaultFocus: "accept",
                acceptClassName: "p-button-danger",
                accept: () => deleteCustomFSTileShop(customTileShopId!),
                dismissableMask: true,
                resizable: false,
                draggable: false,
            });
        }
    }

    const deleteCustomFSTileShop = async (customTileShopId: number) => {
        // if (customTileShopId) {
        //     setDeleteTileShopLoadingId(customTileShopId);
        //     const result = await; // api here

        //     setDeleteTileShopLoadingId(null);
        //     if (result.statusCode === 200) {
        //         fsTileShopsDataGet();
        //         setSelectedFSTileShop(null);

        //         if (toast.current) {
        //             toast.current.show({
        //                 severity: "success",
        //                 summary: "Success",
        //                 detail: "Shop deleted successfully.",
        //                 life: 3000,
        //             });
        //         }
        //     } else {
        //         if (toast.current) {
        //             toast.current.show({
        //                 severity: "error",
        //                 summary: "Failed",
        //                 detail: "There was an error deleting the shop.",
        //                 life: 3000,
        //             });
        //         }
        //     }
        // }
    }
    /* ************************************************************* */


    /* ************************************************************* Door feature */
    /* ************************************************************* */

    /* ************************************************************* Paint feature */
    /* ************************************************************* */

    /* ************************************************************* Tiles feature */
    // Tile location
    const handleCloseTileModal = () => {
        setShowTilesModal(false);
        handleResetTileLocationFields();
    }

    const handleResetTileLocationFields = () => {
        setTileLocation('');
        setTileLocationError('');
    }

    useEffect(() => {
        checkText(tileLocation, false, setTileLocationError);
    }, [tileLocation]);

    function checkCredentialsForTileLocation(): boolean {
        const isTileLocationValid = checkText(tileLocation, true, setTileLocationError);

        return isTileLocationValid;
    }

    function showAPIErrorForTileLocation(keyName: string, error: string): void {
        if (keyName === "location_name") {
            setTileLocationError(error);
        }
    }

    const handleAddTileLocation = () => {
        setShowTilesModal(true);
        setDataState('Add');
    }

    const handleCreateTileLocation = async (e: React.FormEvent) => {
        e.preventDefault();

        // if (checkCredentialsForTileLocation()) {
        //     setLoading(true);
        //     const data = await ; //api here
        //     setLoading(false);

        //     if (data.statusCode === 200) {
        //         fsTileDataGet();
        //         handleCloseTileModal();

        //         if (toast.current) {
        //             toast.current?.show({
        //                 severity: "success",
        //                 summary: "Success",
        //                 detail: `Tile location added successfully.`,
        //                 life: 3000,
        //             });
        //         }
        //     } else if (data.statusCode === 422) {
        //         Object.entries(data.errors ?? []).map(([key, value], index) => (
        //             showAPIErrorForTileLocation(key, value[0])
        //         ));
        //     } else {
        //         if (toast.current) {
        //             toast.current.show({
        //                 severity: "error",
        //                 summary: "Failed",
        //                 detail: "There was an error adding the tile location.",
        //                 life: 3000,
        //             });
        //         }
        //     }
        // }
    }

    const handleEditTileLocation = (tile: FSTile) => {
        if (tile) {
            setShowTilesModal(true);
            setDataState('Edit');

            setSelectedTile(tile);
            setTileLocation(tile?.location_name || '')
        }
    }

    useEffect(() => {
        if (selectedTile) {
            if (tileLocation !== selectedTile?.location_name) {
                setTileLocationUpdateHasChanges(true);
            } else {
                setTileLocationUpdateHasChanges(false);
            }
        }
    }, [selectedTile, tileLocation]);

    const handleUpdateTileLocation = async (e: React.FormEvent) => {
        e.preventDefault();

        // if (checkCredentialsForTileLocation()) {
        //     setLoading(true);
        //     const data = await ; //api here
        //     setLoading(false);

        //     if (data.statusCode === 200) {
        //         fsTileDataGet();
        //         handleCloseTileModal();

        //         if (toast.current) {
        //             toast.current?.show({
        //                 severity: "success",
        //                 summary: "Success",
        //                 detail: `Tile location updated successfully.`,
        //                 life: 3000,
        //             });
        //         }
        //     } else if (data.statusCode === 422) {
        //         Object.entries(data.errors ?? []).map(([key, value], index) => (
        //             showAPIErrorForTileLocation(key, value[0])
        //         ));
        //     } else {
        //         if (toast.current) {
        //             toast.current.show({
        //                 severity: "error",
        //                 summary: "Failed",
        //                 detail: "There was an error updating the tile location.",
        //                 life: 3000,
        //             });
        //         }
        //     }
        // }
    }

    const handleDeleteTileLocation = (tile: FSTile) => {
        const tileLocationId = tile?.id;

        confirmDialog({
            message: `Are you sure you want to delete the ${tile?.location_name} location`,
            header: "Confirm the deletion",
            icon: "bi bi-info-circle",
            defaultFocus: "accept",
            acceptClassName: "p-button-danger",
            accept: () => deleteTileLocation(tileLocationId),
            resizable: false,
            draggable: false,
            dismissableMask: true,
        });
    }

    const deleteTileLocation = async (tileLocationId: number) => {
        // if (tileLocationId) {
        //     setTileLocationDeleteLoadingId(tileLocationId);
        //     const data = await ; // api here

        //     setTileLocationDeleteLoadingId(null);
        //     if (data.statusCode === 200) {
        //         fsTileDataGet();

        //         if (toast.current) {
        //             toast.current?.show({
        //                 severity: "success",
        //                 summary: "Success",
        //                 detail: `Tile location deleted successfully.`,
        //                 life: 3000,
        //             });
        //         }
        //     } else {
        //         if (toast.current) {
        //             toast.current.show({
        //                 severity: "error",
        //                 summary: "failed",
        //                 detail: "An error occurred while deleting the tile location.",
        //                 life: 3000,
        //             });
        //         }
        //     }
        // }
    }

    /* Modal header */
    const tileModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                {dataState}&nbsp; tile location
            </h5>
            <button type="button" className="close_modal_btn p-ripple"
                onClick={handleCloseTileModal}>
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    );

    /* Modal footer */
    const tileModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Close"
                className="custom_btn secondary"
                onClick={handleCloseTileModal}
            />

            <Button
                label={loading ? 'Processing' : dataState === 'Add' ? 'Save' : 'Update'}
                onClick={dataState === 'Add' ? handleCreateTileLocation : handleUpdateTileLocation}
                loading={loading}
                className="custom_btn primary"
                disabled={dataState === 'Edit' && !tileLocationUpdateHasChanges}
            />
        </div>
    );
    // 

    // Tile details
    const handleCloseTileDetailModal = () => {
        setShowTileDetailModal(true);
        handleResetTileDetailFields();
    }

    const handleResetTileDetailFields = () => {
        setSelectedFSType(null);
        setTileName('');
        setTileQuantity(null);
        setSelectedTileShop(null);
        setSelectedTileColor(null);
        setTileSize(null);
        setTileLink('');
        setTileImageFile(null);
        setTileImageFileName('');
        setTileFiles([]);

        setSelectedTile(null);
        setSelectedTileDetail(null);

        setTileTypeError('');
        setTileNameError('');
        setTileQuantityError('');
        setTileShopError('');
        setTileColorError('');
        setTileSizeError('');
        setTileLinkError('');
        setTileImageFileError('');
        setTileFilesError('');
    }

    useEffect(() => {
        checkModelSelected(selectedFSType != null, false, setTileTypeError);
    }, [selectedFSType]);

    useEffect(() => {
        checkText(tileName, false, setTileNameError);
    }, [tileName]);

    useEffect(() => {
        checkNumber(tileQuantity!, false, setTileQuantityError);
    }, [tileQuantity]);

    useEffect(() => {
        checkModelSelected(selectedTileShop != null, false, setTileShopError);
    }, [selectedTileShop]);

    useEffect(() => {
        checkModelSelected(selectedTileColor != null, false, setTileColorError);
    }, [selectedTileColor]);

    useEffect(() => {
        checkNumber(tileSize!, false, setTileSizeError);
    }, [tileSize]);

    useEffect(() => {
        checkText(tileLink, false, setTileLinkError);
    }, [tileLink]);

    useEffect(() => {
        checkFileOthers(tileImageFile!, false, setTileImageFileError);
    }, [tileImageFile]);

    useEffect(() => {
        checkFileOtherList(tileFiles!, false, setTileFilesError);
    }, [tileFiles]);

    function checkCredentialsForTileDetail(fileRequired: boolean): boolean {
        const isTileTypeValid = checkModelSelected(selectedFSType != null, true, setTileTypeError);
        const isTileNameValid = checkText(tileName, true, setTileNameError);
        const isTileQuantityValid = checkNumber(tileQuantity!, true, setTileQuantityError);
        const isTileShopValid = checkModelSelected(selectedTileShop != null, true, setTileShopError);
        const isTileColorValid = checkModelSelected(selectedTileColor != null, true, setTileColorError);
        const isTileLinkValid = checkText(tileLink, true, setTileLinkError);
        const isTileSizeValid = checkNumber(tileSize!, true, setTileSizeError);
        const isTileImageValid = checkFileOthers(tileImageFile!, false, setTileImageFileError);
        const isTileFilesValid = checkFileOtherList(tileFiles!, false, setTileFilesError);

        return (
            isTileTypeValid &&
            isTileNameValid &&
            isTileQuantityValid &&
            isTileShopValid &&
            isTileColorValid &&
            isTileLinkValid &&
            isTileSizeValid &&
            isTileImageValid &&
            isTileFilesValid
        );
    }

    function showAPIErrorForTileDetail(keyName: string, error: string): void {
        if (keyName === "type_id") {
            setTileTypeError(error);
        }

        if (keyName === "name") {
            setTileNameError(error);
        }

        if (keyName === "quantity") {
            setTileQuantityError(error);
        }

        if (keyName === "shop_id") {
            setTileShopError(error);
        }

        if (keyName === "color_id") {
            setTileColorError(error);
        }

        if (keyName === "size") {
            setTileSizeError(error);
        }

        if (keyName === "link") {
            setTileLinkError(error);
        }

        if (keyName === "image_path") {
            setTileImageFileError(error);
        }

        if (keyName === "file_list[]") {
            setTileFilesError(error);
        }
    }

    const handleAddTileDetail = (tile: FSTile) => {
        setSelectedTile(tile);
        setShowTileDetailModal(true);
        setDataState('Add');
    }

    const handleCreateTileDetail = async (e: React.FormEvent) => {
        e.preventDefault();

        // if (checkCredentialsForTileDetail(true)) {
        //     setLoading(true);
        //     const data = await ; //api here
        //     setLoading(false);

        //     if (data.statusCode === 200) {
        //         fsTileDataGet();
        //         handleCloseTileDetailModal();

        //         if (toast.current) {
        //             toast.current?.show({
        //                 severity: "success",
        //                 summary: "Success",
        //                 detail: `Tile detail added successfully.`,
        //                 life: 3000,
        //             });
        //         }
        //     } else if (data.statusCode === 422) {
        //         Object.entries(data.errors ?? []).map(([key, value], index) => (
        //             showAPIErrorForTileDetail(key, value[0])
        //         ));
        //     } else {
        //         if (toast.current) {
        //             toast.current.show({
        //                 severity: "error",
        //                 summary: "Failed",
        //                 detail: "There was an error adding the tile detail.",
        //                 life: 3000,
        //             });
        //         }
        //     }
        // }
    }

    useEffect(() => {
        if (selectedTileDetail) {
            if (selectedFSType?.id !== selectedTileDetail?.type_id) {
                setTileDetailUpdateHasChanges(true);
            } else if (tileName !== selectedTileDetail?.name) {
                setTileDetailUpdateHasChanges(true);
            } else if (tileQuantity !== null && tileQuantity !== selectedTileDetail?.quantity) {
                setTileDetailUpdateHasChanges(true);
            } else if (selectedTileShop?.id !== selectedTileDetail?.shop_id) {
                setTileDetailUpdateHasChanges(true);
            } else if (selectedTileColor?.id !== selectedTileDetail?.color_id) {
                setTileDetailUpdateHasChanges(true);
            } else if (tileSize !== null && tileSize !== selectedTileDetail?.size) {
                setTileDetailUpdateHasChanges(true);
            } else if (tileLink !== null && tileLink !== selectedTileDetail?.link) {
                setTileDetailUpdateHasChanges(true);
            } else if (tileImageFile !== null) {
                setTileDetailUpdateHasChanges(true);
            } else if (tileFiles?.length > 0) {
                setTileDetailUpdateHasChanges(true);
            } else {
                setTileDetailUpdateHasChanges(false);
            }
        }
    }, [
        selectedTileDetail,
        selectedFSType,
        tileName,
        tileQuantity,
        selectedTileShop,
        selectedTileColor,
        tileSize,
        tileLink,
        tileImageFile,
        tileFiles
    ]);

    const handleEditTileDetail = (tileDetail: FSTileData) => {
        if (tileDetail) {
            setShowTileDetailModal(true);
            setDataState('Edit');

            setSelectedTileDetail(tileDetail);

            const existTileType = fsTypes?.find(type => type?.id === tileDetail?.type_id);
            const existTileShop = fsTileShops?.find(shop => shop?.id === tileDetail?.shop_id);
            const existTileColor = fsColors?.find(color => color?.id === tileDetail?.color_id);

            setSelectedFSType(existTileType || null);
            setTileName(tileDetail?.name || '');
            setTileQuantity(tileDetail?.quantity || null);
            setSelectedTileShop(existTileShop || null);
            setSelectedTileColor(existTileColor || null);
            setTileSize(tileDetail?.size || null);
            setTileLink(tileDetail?.link || '');
        }
    }

    const handleUpdateTileDetail = async (e: React.FormEvent) => {
        e.preventDefault();

        // if (checkCredentialsForTileDetail(false)) {
        //     setLoading(true);
        //     const data = await ; //api here
        //     setLoading(false);

        //     if (data.statusCode === 200) {
        //         fsTileDataGet();
        //         handleCloseTileDetailModal();

        //         if (toast.current) {
        //             toast.current?.show({
        //                 severity: "success",
        //                 summary: "Success",
        //                 detail: `Tile detail updated successfully.`,
        //                 life: 3000,
        //             });
        //         }
        //     } else if (data.statusCode === 422) {
        //         Object.entries(data.errors ?? []).map(([key, value], index) => (
        //             showAPIErrorForTileDetail(key, value[0])
        //         ));
        //     } else {
        //         if (toast.current) {
        //             toast.current.show({
        //                 severity: "error",
        //                 summary: "Failed",
        //                 detail: "There was an error updating the tile detail.",
        //                 life: 3000,
        //             });
        //         }
        //     }
        // }
    }

    const handleDeleteTileDetail = (tileDetail: FSTileData) => {
        const tileDetailId = tileDetail?.id;

        confirmDialog({
            message: `Are you sure you want to delete this tile data?`,
            header: "Confirm the deletion",
            icon: "bi bi-info-circle",
            defaultFocus: "accept",
            acceptClassName: "p-button-danger",
            accept: () => deleteTileDetail(tileDetailId),
            resizable: false,
            draggable: false,
            dismissableMask: true,
        });
    }

    const deleteTileDetail = async (tileDetailId: number) => {
        // if (tileDetailId) {
        //     setTileDetailDeleteLoadingId(tileDetailId);
        //     const data = await ; // api here

        //     setTileDetailDeleteLoadingId(null);
        //     if (data.statusCode === 200) {
        //         fsTileDataGet();

        //         if (toast.current) {
        //             toast.current?.show({
        //                 severity: "success",
        //                 summary: "Success",
        //                 detail: `Tile detail deleted successfully.`,
        //                 life: 3000,
        //             });
        //         }
        //     } else {
        //         if (toast.current) {
        //             toast.current.show({
        //                 severity: "error",
        //                 summary: "failed",
        //                 detail: "An error occurred while deleting the tile detail.",
        //                 life: 3000,
        //             });
        //         }
        //     }
        // }
    }

    /* Modal header */
    const tileDetailModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                {dataState}&nbsp; tile detail {dataState === 'Add' ? ` - ${selectedTile?.location_name}` : ''}
            </h5>
            <button type="button" className="close_modal_btn p-ripple"
                onClick={handleCloseTileDetailModal}>
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    );

    /* Modal footer */
    const tileDetailModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Close"
                className="custom_btn secondary"
                onClick={handleCloseTileDetailModal}
            />

            <Button
                label={loading ? 'Processing' : dataState === 'Add' ? 'Save' : 'Update'}
                onClick={dataState === 'Add' ? handleCreateTileDetail : handleUpdateTileDetail}
                loading={loading}
                className="custom_btn primary"
                disabled={dataState === 'Edit' && !tileDetailUpdateHasChanges}
            />
        </div>
    );
    /* ************************************************************* */

    /* ************************************************************* Floor finishing feature */
    /* ************************************************************* */

    const handleNavigateFSDocument = () => {
        navigate(`document`, { state: { projectData: finishingScheduleProject, fsFeatre: activeFSFeature } })
    }

    const handleNavigateFSSummary = () => {
        navigate(`summary`, { state: { projectData: finishingScheduleProject, fsFeatre: activeFSFeature } })
    }

    return (
        <React.Fragment>
            <Toast ref={toast} />

            <div className="main_content layout_padding show_side_bar">
                {loadingFSProjects && initialDataLoading ? (
                    <PageLoader />
                ) : errorFSProjects ? (
                    <ErrorPage errorMessage={errorFSProjects} />
                ) : (
                    <div className="page_content mt-0">
                        <div className="page_breadcrumb_area">
                            {navigateFrom && navigateFrom === 'Project' ? (
                                <ul className="page_breadcrumb">
                                    <li className="page_breadcrumb_item has_child has_link" onClick={() => navigate(-1)}>
                                        {finishingScheduleProject?.job_number + ": " + finishingScheduleProject?.address}
                                    </li>
                                    <li className="page_breadcrumb_item active">
                                        Finishing schedule
                                    </li>
                                </ul>
                            ) : (
                                <ul className="page_breadcrumb">
                                    <li className="page_breadcrumb_item has_child has_link" onClick={() => navigate(-1)}>
                                        Projects
                                    </li>
                                    <li className="page_breadcrumb_item has_child">
                                        {finishingScheduleProject?.job_number + ": " + finishingScheduleProject?.address}
                                    </li>
                                    <li className="page_breadcrumb_item active">
                                        Finishing schedule
                                    </li>
                                </ul>
                            )}

                            <button className="back_btn p-ripple" onClick={goBack}>
                                <i className="bi bi-arrow-left me-2"></i>
                                Back
                                <Ripple />
                            </button>
                        </div>

                        <div className="page_header_area scrollable custom_wrap">
                            <div className="main_heading_tab_area custom">
                                {finishingScheduleFeatures && finishingScheduleFeatures.length > 0 && (
                                    <React.Fragment>
                                        {finishingScheduleFeatures?.map((feature) => (
                                            <React.Fragment>
                                                <button
                                                    key={feature.id}
                                                    className={`main_heading_as_tab is_tab ${activeFSFeature?.id === feature?.id ? "active" : ''} p-ripple is_btn text-capitalize`}
                                                    type="button"
                                                    role="tab"
                                                    onClick={() => handleSelectFSFeature(feature)}
                                                >
                                                    {feature.feature}
                                                    < Ripple />
                                                </button>

                                                <div className="tab_divider"></div>
                                            </React.Fragment>
                                        ))}
                                    </React.Fragment>
                                )}
                            </div>

                            <div className="page_action_area">
                                <button
                                    className="add_data_link_btn custom p-ripple"
                                    onClick={handleNavigateFSSummary}
                                >
                                    <i className="bi bi-file-text"></i>
                                    <span className="btn_text">Summary</span>
                                    <Ripple />
                                </button>

                                <button
                                    className="add_data_link_btn custom p-ripple"
                                    onClick={handleNavigateFSDocument}
                                >
                                    <i className="bi bi-file-earmark-text"></i>
                                    <span className="btn_text">Document</span>
                                    <Ripple />
                                </button>

                                {activeFSFeature?.feature === 'Tiles' && hasPermissionForAddTileLocation && (
                                    <button
                                        className="add_data_link_btn custom p-ripple"
                                        onClick={handleAddTileLocation}
                                    >
                                        <i className="bi bi-plus-circle"></i>
                                        <span className="btn_text">Add</span>
                                        <Ripple />
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>


            {/* Tile location modal */}
            <Dialog
                visible={showTilesModal}
                header={tileModalHeader}
                footer={tileModalFooter}
                headerClassName="custom_modal_header"
                className="custom_modal_dialog modal_dialog_sm"
                onHide={handleCloseTileModal}
                dismissableMask>
                <div className="custom_modal_body">
                    <div className="p-1 p-sm-4 pt-sm-3 pb-sm-0 w-100">
                        <div className="row">
                            {/* Tile location */}
                            <div className="col-12">
                                <TextInput
                                    key={'tileLocation'}
                                    id="tileLocation"
                                    label="Location"
                                    labelHtmlFor="tileLocation"
                                    required={true}
                                    value={tileLocation}
                                    placeholder="Location"
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTileLocation(e.target.value)}
                                    error={tileLocationError}
                                    formGroupClassName="mb-2"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>
            {/*  */}


            {/* Tile details modal */}
            <Dialog
                visible={showTileDetailModal}
                header={tileDetailModalHeader}
                footer={tileDetailModalFooter}
                headerClassName="custom_modal_header"
                className="custom_modal_dialog modal_dialog_md"
                onHide={handleCloseTileDetailModal}
                dismissableMask>
                <div className="custom_modal_body">
                    <div className="p-1 p-sm-4 pt-sm-3 pb-sm-0 w-100">
                        <div className="row">
                            <div className="col-12 col-sm-6">

                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>
            {/*  */}


            {/* Type list modal */}
            <Dialog
                visible={showTypeListModal}
                headerClassName="d-none"
                dismissableMask
                className="custom_modal_dialog modal_dialog_sm data_select_modal"
                contentClassName="data_select_modal_content"
                onHide={handleCloseTypeListModal}
            >
                <div className="custom_modal_body data_select_modal_area">
                    <div className="data_select_modal_header">
                        <div className="data_search_input_area">
                            <InputText
                                key={"fsTypeSearchKey"}
                                id="fsTypeSearchKey"
                                value={fsTypeSearchKey}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFSTypeSearchKey(e.target.value)}
                                className="custom_form_input data_search_input"
                                placeholder="Search type by name"
                                autoComplete="off"
                                autoFocus
                                type={"search"}
                            />

                            {fsTypeSearchKey === "" ? (
                                <i className="bi bi-search"></i>
                            ) : (
                                <i
                                    className="bi bi-x-lg data_search_input_clear"
                                    onClick={() => setFSTypeSearchKey("")}
                                ></i>
                            )}
                        </div>

                        <button
                            type="button"
                            className="data_select_modal_close p-ripple"
                            onClick={handleCloseTypeListModal}
                        >
                            <i className="bi bi-x"></i>
                            <Ripple />
                        </button>
                    </div>

                    <div className="data_select_modal_body has_custom_input">
                        {fsTypesData && fsTypesData?.length > 0 ? (
                            <div className="data_select_option_area">
                                {fsTypesData?.map((type) => (
                                    <div
                                        key={`type_${type?.id}`}
                                        className={`data_select_option p-ripple ${type?.id === selectedFSType?.id && "selected"}
                                        ${type?.created_by !== null ? 'custom' : ''}`}
                                        onClick={() => handleSelectType(type)}
                                    >
                                        <span>{type?.type_name}</span>
                                        <Ripple pt={{ root: { style: { background: "rgba(164, 96, 253, 0.2)" } } }} />

                                        {type?.created_by !== null && (
                                            <div className="custom_data_action_area">
                                                {hasPermissionForUpdateType && (
                                                    <button className="is_btn data_select_option_edit_btn p-ripple"
                                                        onClick={(e) => {
                                                            e.stopPropagation();

                                                            if (selectedCustomType !== null) {
                                                                handleResetCustomFSTypeFields();
                                                            } else {
                                                                handleEditCustomType(type);
                                                            }
                                                        }}>
                                                        <i className={`bi ${(selectedCustomType !== null && selectedCustomType?.id === type?.id) ? 'bi-x-lg' : 'bi-pencil-square'}`}></i>
                                                    </button>
                                                )}

                                                {hasPermissionForDeleteType && (
                                                    <button className="is_btn data_select_option_edit_btn delete p-ripple"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDeleteCustomFSType(type);
                                                        }}
                                                        disabled={deleteTypeLoadingId === type?.id}
                                                    >
                                                        <i className={deleteTypeLoadingId === type?.id ? 'ri-loader-4-fill loading_spinner' : 'bi bi-trash'}></i>
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="no_records_found">
                                <i className="bi bi-exclamation-circle me-1"></i>
                                No types found. {hasPermissionForAddType ? 'To add the type click on add button' : ''}
                            </div>
                        )}
                    </div>

                    {hasPermissionForAddType && (
                        <div className="page_form_group data_select_modal_footer mb-0">
                            <div className="input_with_button_area">
                                <InputText
                                    key={`customTypeName`}
                                    id="customTypeName"
                                    value={customTypeName}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCustomTypeName(e.target.value)}
                                    className="custom_form_input has_add_value custom_data_input"
                                    placeholder="Enter type name"
                                    autoComplete="off"
                                />
                                <button
                                    className="add_value_btn fill p-ripple"
                                    onClick={customDataState === 'Add' ? handleCreateCustomFSType : handleUpdateCustomFSType}
                                    disabled={(customLoading || customTypeName === "") || (customDataState === 'Edit' && !customTypeUpdateHasChanges)}
                                >
                                    <i className={customLoading ? "ri-loader-4-fill loading_spinner" : customDataState === 'Edit' ? "bi bi-pencil-square" : "bi bi-plus-lg"}></i>
                                    <span>{customLoading ? "Processing" : customDataState === 'Edit' ? "Update" : "Add"}</span>
                                    <Ripple />
                                </button>
                            </div>
                            {customTypeNameError && (
                                <small className="form_error_msg">
                                    {customTypeNameError}
                                </small>
                            )}
                        </div>
                    )}
                </div>
            </Dialog>
            {/*  */}


            {/* Tile shop list modal */}
            <Dialog
                visible={showTileShopListModal}
                headerClassName="d-none"
                dismissableMask
                className="custom_modal_dialog modal_dialog_sm data_select_modal"
                contentClassName="data_select_modal_content"
                onHide={handleCloseTileShopListModal}
            >
                <div className="custom_modal_body data_select_modal_area">
                    <div className="data_select_modal_header">
                        <div className="data_search_input_area">
                            <InputText
                                key={"fsTileShopSearchKey"}
                                id="fsTileShopSearchKey"
                                value={fsTileShopSearchKey}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFSTileShopSearchKey(e.target.value)}
                                className="custom_form_input data_search_input"
                                placeholder="Search shop by name"
                                autoComplete="off"
                                autoFocus
                                type={"search"}
                            />

                            {fsTileShopSearchKey === "" ? (
                                <i className="bi bi-search"></i>
                            ) : (
                                <i
                                    className="bi bi-x-lg data_search_input_clear"
                                    onClick={() => setFSTileShopSearchKey("")}
                                ></i>
                            )}
                        </div>

                        <button
                            type="button"
                            className="data_select_modal_close p-ripple"
                            onClick={handleCloseTileShopListModal}
                        >
                            <i className="bi bi-x"></i>
                            <Ripple />
                        </button>
                    </div>

                    <div className="data_select_modal_body has_custom_input">
                        {fsTileShopsData && fsTileShopsData?.length > 0 ? (
                            <div className="data_select_option_area">
                                {fsTileShopsData?.map((shop) => (
                                    <div
                                        key={`shop_${shop?.id}`}
                                        className={`data_select_option p-ripple ${shop?.id === selectedFSTileShop?.id && "selected"}
                                        ${shop?.created_by !== null ? 'custom' : ''}`}
                                        onClick={() => handleSelectTileShop(shop)}
                                    >
                                        <span>{shop?.shop_name}</span>
                                        <Ripple pt={{ root: { style: { background: "rgba(164, 96, 253, 0.2)" } } }} />

                                        {shop?.created_by !== null && (
                                            <div className="custom_data_action_area">
                                                {hasPermissionForUpdateTileShop && (
                                                    <button className="is_btn data_select_option_edit_btn p-ripple"
                                                        onClick={(e) => {
                                                            e.stopPropagation();

                                                            if (selectedCustomTileShop !== null) {
                                                                handleResetCustomFSTileShopFields();
                                                            } else {
                                                                handleEditCustomTileShop(shop);
                                                            }
                                                        }}>
                                                        <i className={`bi ${(selectedCustomTileShop !== null && selectedCustomTileShop?.id === shop?.id) ? 'bi-x-lg' : 'bi-pencil-square'}`}></i>
                                                    </button>
                                                )}

                                                {hasPermissionForDeleteTileShop && (
                                                    <button className="is_btn data_select_option_edit_btn delete p-ripple"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDeleteCustomFSTileShop(shop);
                                                        }}
                                                        disabled={deleteTileShopLoadingId === shop?.id}
                                                    >
                                                        <i className={deleteTileShopLoadingId === shop?.id ? 'ri-loader-4-fill loading_spinner' : 'bi bi-trash'}></i>
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="no_records_found">
                                <i className="bi bi-exclamation-circle me-1"></i>
                                No shops found. {hasPermissionForAddTileShop ? 'To add the shop click on add button.' : ''}
                            </div>
                        )}
                    </div>

                    {hasPermissionForAddTileShop && (
                        <div className="page_form_group data_select_modal_footer mb-0">
                            <div className="input_with_button_area">
                                <InputText
                                    key={`customTileShopName`}
                                    id="customTileShopName"
                                    value={customTileShopName}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCustomTileShopName(e.target.value)}
                                    className="custom_form_input has_add_value custom_data_input"
                                    placeholder="Enter shop name"
                                    autoComplete="off"
                                />
                                <button
                                    className="add_value_btn fill p-ripple"
                                    onClick={customDataState === 'Add' ? handleCreateCustomFSTileShop : handleUpdateCustomFSTileShop}
                                    disabled={(customLoading || customTileShopName === "") || (customDataState === 'Edit' && !customTileShopUpdateHasChanges)}
                                >
                                    <i className={customLoading ? "ri-loader-4-fill loading_spinner" : customDataState === 'Edit' ? "bi bi-pencil-square" : "bi bi-plus-lg"}></i>
                                    <span>{customLoading ? "Processing" : customDataState === 'Edit' ? "Update" : "Add"}</span>
                                    <Ripple />
                                </button>
                            </div>
                            {customTileShopNameError && (
                                <small className="form_error_msg">
                                    {customTileShopNameError}
                                </small>
                            )}
                        </div>
                    )}
                </div>
            </Dialog>
            {/*  */}
        </React.Fragment>
    )
}

export default FinishingSchedule;